import React from 'react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogIn, LayoutDashboard, Settings, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useInteractionTracking } from '@/hooks/useInteractionTracking';

export const CornerIcon = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { trackClick, trackNavigationOnly } = useInteractionTracking('CornerIcon');

  const handleSignIn = () => {
    trackClick('signin-btn', 'button', {
      location: 'corner-navigation',
      action: 'navigate-to-auth',
      context: 'landing-page'
    });
    trackNavigationOnly('/auth', { source: 'corner-icon' });
    navigate('/auth');
  };

  const handleDashboard = () => {
    trackClick('dashboard-menu-item', 'button', {
      location: 'user-dropdown',
      action: 'navigate-to-dashboard',
      context: 'landing-page'
    });
    trackNavigationOnly('/dashboard', { source: 'corner-icon-menu' });
    navigate('/dashboard');
  };

  const handleSignOut = () => {
    trackClick('signout-btn', 'button', {
      location: 'user-dropdown',
      action: 'sign-out',
      context: 'landing-page'
    });
    signOut();
  };

  const getUserInitials = () => {
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="fixed top-6 right-8 z-[60]">
      {user ? (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="relative h-12 w-12 rounded-full bg-white shadow-[4px_4px_8px_#e5e7eb,-4px_-4px_8px_#ffffff] hover:shadow-[2px_2px_4px_#e5e7eb,-2px_-2px_4px_#ffffff] transition-all duration-200 will-change-transform"
              aria-label="User profile menu"
            >
              <Avatar className="h-10 w-10">
                <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email} />
                <AvatarFallback className="bg-slate-900 text-white shadow-[inset_2px_2px_4px_rgba(255,255,255,0.1)]">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 bg-white shadow-[8px_8px_16px_#d1d5db,-8px_-8px_16px_#ffffff] border border-gray-200/50 rounded-2xl z-[70]"
            align="end"
            sideOffset={8}
          >
            <div className="flex items-center justify-start gap-2 p-2">
              <div className="flex flex-col space-y-1 leading-none">
                <p className="font-medium text-sm text-foreground">{user.email}</p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDashboard} className="cursor-pointer text-gray-700 hover:bg-gray-50 rounded-xl mx-2 my-1 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.05)] hover:shadow-[inset_4px_4px_8px_rgba(0,0,0,0.05)] min-h-[44px]">
              <LayoutDashboard className="mr-2 h-5 w-5 min-w-[20px] min-h-[20px]" />
              Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                trackClick('profile-menu-item', 'button', {
                  location: 'user-dropdown',
                  action: 'navigate-to-profile',
                  context: 'landing-page'
                });
                trackNavigationOnly('/profile', { source: 'corner-icon-menu' });
                navigate('/profile');
              }}
              className="cursor-pointer text-gray-700 hover:bg-gray-50 rounded-xl mx-2 my-1 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.05)] hover:shadow-[inset_4px_4px_8px_rgba(0,0,0,0.05)] min-h-[44px]"
            >
              <Settings className="mr-2 h-5 w-5 min-w-[20px] min-h-[20px]" />
              Profile & Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="mx-2 border-gray-200" />
            <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 hover:bg-red-50 rounded-xl mx-2 my-1 shadow-[inset_2px_2px_4px_rgba(239,68,68,0.05)] hover:shadow-[inset_4px_4px_8px_rgba(239,68,68,0.05)] min-h-[44px]">
              <LogOut className="mr-2 h-5 w-5 min-w-[20px] min-h-[20px]" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Button
          onClick={handleSignIn}
          className="flex items-center gap-2 bg-white shadow-[4px_4px_8px_#e5e7eb,-4px_-4px_8px_#ffffff] hover:shadow-[2px_2px_4px_#e5e7eb,-2px_-2px_4px_#ffffff] border-0 text-gray-700 hover:text-gray-900 transition-all duration-200 will-change-transform h-12 min-h-[44px] px-4"
        >
          <LogIn className="w-5 h-5 min-w-[20px] min-h-[20px]" />
          <span className="hidden sm:inline">Sign In</span>
        </Button>
      )}
    </div>
  );
};